/**
 * The system's call UI, for the length of a call.
 *
 * On the web this file is a set of no-ops, the same shape as callAudio.ts and
 * for the same reason: a browser has no lock screen to ring and no Recents to
 * write into. Inside the iOS shell every call is reported to CallKit — the
 * ring from a locked phone, mute and hang-up from the lock screen and CarPlay,
 * the entry in Recents — and this is the whole of the page's side of it.
 * `ios/App/App/CallKitPlugin.swift` is the other end, kept in step by hand.
 *
 * Nothing here is awaited by its callers. A call that did not reach the
 * system UI is a worse call, never a call that fails to start.
 */
import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { nativePushPlugin } from './nativePush.ts';

/** The plugin's name on both sides — `jsName` in CallKitPlugin.swift. */
export const CALL_KIT_PLUGIN = 'CallKit';

/**
 * An action somebody took on the system UI. `at` is when, in epoch
 * milliseconds, because these are drained as well as heard live: a queued
 * answer replayed long after its ring must be recognisable as stale.
 */
export interface CallKitEvent {
  action: 'answer' | 'end' | 'muted' | 'ring-refused';
  callId: string;
  at: number;
  /** Only on 'muted'. */
  on?: boolean;
  /** Only on 'ring-refused': what the system said when it declined to ring. */
  reason?: string;
}

interface CallKitPlugin {
  /** The VoIP token, or why there is none. Resolves either way. */
  register: () => Promise<{ token?: string; error?: string }>;
  /** One path with the VoIP push: a UUID a push already reported is a no-op. */
  reportIncoming: (options: { id: string; name: string; video: boolean }) => Promise<void>;
  reportOutgoing: (options: { id: string; name: string; video: boolean }) => Promise<void>;
  /** Resolves with `error` when the system ring was not dismissed. */
  reportConnected: (options: { id: string }) => Promise<{ error?: string }>;
  endCall: (options: { id: string; reason: string }) => Promise<void>;
  setMuted: (options: { id: string; on: boolean }) => Promise<void>;
  /** Actions taken before JavaScript was awake, cleared as they are read. */
  drainEvents: () => Promise<{ events: CallKitEvent[] }>;
  addListener: (
    event: 'callKitAction',
    handler: (event: CallKitEvent) => void,
  ) => Promise<PluginListenerHandle>;
}

const CallKit = registerPlugin<CallKitPlugin>(CALL_KIT_PLUGIN);

interface CapacitorBridge {
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
}

/**
 * Whether the system's call UI is used at all — under trial, switched off.
 *
 * CallKit buys one thing nothing else can: a ring from a locked, closed
 * phone. What it charges for it turned out to be the whole call experience —
 * the system draws its own screen whenever the app is not in front, so one
 * call had two faces, and the app's own controls (the red button most of all)
 * were behind a screen that offers different ones. Retiring the system call
 * at connect time fixed the overlap and left the first seconds of every
 * answered call still belonging to iOS.
 *
 * So this is the experiment the shape of the feature now depends on: with it
 * off, calls ring the way the pre-CallKit build did — the app's own ringtone
 * when it is open, an ordinary push notification when it is not — and every
 * screen of a call is this app's. Flip it back to `true` (and the matching
 * `CallCentre.enabled` in Swift, which stops PushKit registering) to restore
 * the system UI; the two must agree, because a VoIP push that reaches a
 * device is required by the OS to report a call, and only CallCentre can.
 */
const CALLKIT_ENABLED = false;

/**
 * True only inside the iOS app, and only where the plugin is really
 * registered. Both halves matter for the reason hasNativeCallAudio gives: a
 * web bundle newer than the native half it runs inside must take the web path
 * — keep its own ringtone, skip the reports — rather than call into nothing.
 */
export const hasNativeCallKit: boolean = (() => {
  if (!CALLKIT_ENABLED) return false;
  const capacitor = (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;
  if (capacitor?.getPlatform?.() !== 'ios') return false;
  return capacitor.isPluginAvailable?.(CALL_KIT_PLUGIN) === true;
})();

/** Every report below is this: fire it, and never wait for it. */
function tell(work: () => Promise<unknown>): void {
  if (!hasNativeCallKit) return;
  void work().catch(() => {
    // The system UI missing a fact is a worse call, and there is nothing
    // useful to do about it from here. The plugin logs its own side.
  });
}

export function reportIncomingCall(id: string, name: string, video: boolean): void {
  tell(() => CallKit.reportIncoming({ id, name, video }));
}

export function reportOutgoingCall(id: string, name: string, video: boolean): void {
  tell(() => CallKit.reportOutgoing({ id, name, video }));
}

/**
 * The one report worth hearing the answer to.
 *
 * Dismissing the system's ring for a call answered in the app is a transaction,
 * and a transaction can be refused — after which the lock screen goes on
 * offering accept and decline over a call two people are already talking on,
 * until the server's cancel push dismisses it a second or two later. From the
 * outside that is a slow app; `onProblem` is the only place it is a refusal.
 * Still not awaited by its caller: an answer that resolves late must not hold
 * up the call it belongs to.
 */
export function reportCallConnected(id: string, onProblem?: (reason: string) => void): void {
  if (!hasNativeCallKit) return;
  void CallKit.reportConnected({ id }).then(
    (result) => {
      if (result?.error) onProblem?.(result.error);
    },
    (error: unknown) => onProblem?.(error instanceof Error ? error.message : 'bridge refused'),
  );
}

/**
 * `reason` travels so Recents can be honest: 'hangup' and 'declined' are this
 * person acting and go through the system as such; 'timeout', 'failed' and
 * 'remote' are reported as what happened to them. Swift maps the vocabulary.
 */
export function reportCallEnded(id: string, reason: string): void {
  tell(() => CallKit.endCall({ id, reason }));
}

export function reportCallMuted(id: string, on: boolean): void {
  tell(() => CallKit.setMuted({ id, on }));
}

/** The VoIP token, or null. Never throws — a phone that cannot be rung
 * properly still gets the alert push, and registration must not fail over it. */
export async function registerVoipToken(): Promise<string | null> {
  if (!hasNativeCallKit) return null;
  try {
    const { token } = await CallKit.register();
    return token ?? null;
  } catch {
    return null;
  }
}

/**
 * Live actions, while the page is up. Returns the unsubscribe synchronously,
 * like watchCallAudio and for the same reason: an effect that awaited
 * `addListener` is an effect whose cleanup can run before its subscription
 * exists.
 */
export function watchCallKitActions(handler: (event: CallKitEvent) => void): () => void {
  if (!hasNativeCallKit) return () => {};
  const pending = CallKit.addListener('callKitAction', handler);
  return () => {
    void pending.then((handle) => handle.remove()).catch(() => {});
  };
}

export async function drainCallKitEvents(): Promise<CallKitEvent[]> {
  if (!hasNativeCallKit) return [];
  try {
    const { events } = await CallKit.drainEvents();
    return events;
  } catch {
    return [];
  }
}

/**
 * The same actions, pressed on a notification instead of on a system call
 * screen.
 *
 * Android has no CallKit and does not need one: its notification carries the
 * two buttons itself (`CallStyle` in ChatNotifications.kt), and what a press
 * leaves behind is a queue in the push plugin, in this exact shape — so
 * `reduceCallKitEvents` folds both sources without a branch, and the tests it
 * already has cover both. iOS will answer here too once its own actions land;
 * a plugin that has never heard of the method simply throws, and a shell older
 * than this bundle is the case that catch is for.
 */
export async function drainNativePushCallActions(): Promise<CallKitEvent[]> {
  if (!hasNativePushCallActions) return [];
  try {
    const { events } = await nativePushPlugin()!.drainCallActions();
    return events as CallKitEvent[];
  } catch {
    return [];
  }
}

/** Whether this shell has the notification-button queue at all. */
export const hasNativePushCallActions: boolean = nativePushPlugin() !== null;

// ---- the joinable half: what a batch of events amounts to ----

/**
 * How long an answer stays actionable. A shade over the server's 60-second
 * ring: an answer older than any possible ring is one whose call has already
 * been recorded as missed, and acting on it would ring a ghost.
 */
export const ANSWER_TTL_MS = 90_000;

export interface CallKitActions {
  /**
   * The call to answer, if an unexpired answer survived everything after it.
   * On a cold start this is the whole point: the tap happened before
   * JavaScript ran a line, and the call it names arrives moments later over
   * the fresh socket as `call:incoming`.
   */
  answer: { callId: string; at: number } | null;
  /** Calls somebody ended or declined on the system UI. */
  ended: string[];
  /** The last mute state per call, for the echo-guarded toggle. */
  muted: Map<string, boolean>;
  /**
   * Rings the system declined to show, surfaced so the page can file them.
   * A refused `reportNewIncomingCall` — Do-Not-Disturb and its relatives —
   * was an NSLog nobody reads, and it is one of the ways a call ends up
   * «callkit does not know this call» when the page later answers it.
   */
  refusals: { callId: string; reason: string }[];
}

/**
 * Folds a drained batch into what should happen now. Pure, and here rather
 * than in CallContext, because this is the half that wants tests: the same
 * events arrive live one at a time and drained twenty at a time, and both
 * paths must agree — an answer followed by its own end is no answer at all.
 */
export function reduceCallKitEvents(events: CallKitEvent[], now: number): CallKitActions {
  let answer: CallKitActions['answer'] = null;
  const ended: string[] = [];
  const muted = new Map<string, boolean>();
  const refusals: CallKitActions['refusals'] = [];

  for (const event of events) {
    if (event.action === 'answer') {
      if (now - event.at < ANSWER_TTL_MS) answer = { callId: event.callId, at: event.at };
      continue;
    }
    if (event.action === 'end') {
      if (answer?.callId === event.callId) answer = null;
      if (!ended.includes(event.callId)) ended.push(event.callId);
      continue;
    }
    if (event.action === 'ring-refused') {
      // The same staleness rule as the answer's: a refusal drained days after
      // its ring describes no call anybody is looking at.
      if (now - event.at < ANSWER_TTL_MS) {
        refusals.push({ callId: event.callId, reason: event.reason ?? 'unknown' });
      }
      continue;
    }
    if (event.on !== undefined) muted.set(event.callId, event.on);
  }

  return { answer, ended, muted, refusals };
}

/** Whether a held answer is still worth acting on when its call finally rings. */
export function isFreshAnswer(answer: { at: number }, now: number): boolean {
  return now - answer.at < ANSWER_TTL_MS;
}
